/* eslint-disable no-console */

import {  RestApiErrorResponse, RestErrorSource } from '@tableau/vizportalclient-common-components/lib/src/http/RestApiService';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { Filter } from '~src/commonInfra/utils/Filter';
import { RestApiListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/RestApiListResourceFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { formatFilterClauses, ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { makeRestApiRequest, RestApiRequestConfig } from '~src/httpClient/RestApiService';
import { IGetItemsParams } from '~src/httpClient/serverApi/types/IGetItemsParams';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { LongRunningAction } from '~src/permissions/services/LongRunningAction';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { FilterFieldNames } from '~src/spacesPlatform/models/IFilterClause';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { GetViewsRequest, IGetViewsResult } from '~src/views/ServerApi/requests/GetViewsRequest';

import { IGetDetailBroadcastResult } from '../models/BroadcastModels';
import { BroadcastAsyncPostResponse, BroadcastListViewsResponse, BroadcastPostRequestBody, BroadcastPostRequestParams, BroadcastRestGetResponse as GetBroadcastRestResponse, JobStatusResponse } from '../models/BroadcastRestApiModels';
import { BroadcastServerApiHelper } from '../serverApi/broadcastServerApiHelper';
import { CreateBroadcastViewRequest } from '../serverApi/requests/CreateBroadcastViewRequest';
import { DeleteBroadcastRequest, IDeleteBroadcastResult } from '../serverApi/requests/DeleteBroadcastRequest';
import { GetBroadcastViewsRequest } from '../serverApi/requests/GetBroadcastViewsRequest';
import { GetDetailOfABroadcastRequest } from '../serverApi/requests/GetDetailOfABroadcastRequest';


class BroadcastsResource {
    private getDeprecatedBroadcastViews = new WebClientListResourceFetcher(GetBroadcastViewsRequest,
        BroadcastServerApiHelper.mapViewsFromResult);

    private getBroadcastViewsRest = new RestApiListResourceFetcher(this.getViewsFromListBroadcastViews,
        (getViewResult) => BroadcastServerApiHelper.mapViewsFromResult(getViewResult)
    );

    public getAllBroadcasts(): ResourceQuery<IContentInfo> {
        if (FeatureFlags.broadcastUIRest) {
            return new ResourceQuery<IContentInfo>(this.getBroadcastViewsRest);
        }

        return new ResourceQuery<IContentInfo>(this.getDeprecatedBroadcastViews);
    }

    public getDetailOfABroadcast(viewLuid: string): Promise<IGetDetailBroadcastResult> {

        if (FeatureFlags.broadcastUIRest) {

            const config: RestApiRequestConfig<{}, {}> = {
                ...createBaseRestConfig(),
                httpMethod: 'GET',
                endpoint: `broadcasts/views/${viewLuid}`,
            };

            let getBroadcastResponse = makeRestApiRequest<{}, GetBroadcastRestResponse, {}>(config);
            return getBroadcastResponse.then((result: GetBroadcastRestResponse) => {
                return BroadcastServerApiHelper.mapGetBroadcastRestResponseToIBroadcast(result);
            }, (error) => Promise.reject(error));
        }

        const request = new GetDetailOfABroadcastRequest({ viewLuid: viewLuid });
        return WebHttpClientWithErrorToastInstance.sendRequest(request);
    }

    public async createBroadcast(viewId: string, viewLuid: string, showTabs: boolean, showWatermark: boolean, overwrite: boolean, acceptTermsOfUse: boolean): Promise<any> {

        if (FeatureFlags.broadcastUIRest) {
            const config: RestApiRequestConfig<BroadcastPostRequestBody, BroadcastPostRequestParams> = {
                ...createBaseRestConfig(),
                httpMethod: 'POST',
                endpoint: `broadcasts/views/`,
                requestBody: { broadcastViewSend: { viewId: viewLuid, showTabs: showTabs, showWatermark: showWatermark } },
                queryParams: { acceptTermsOfUse: acceptTermsOfUse, overwrite: overwrite, asJob: true }
            };

            const result = await makeRestApiRequest<BroadcastPostRequestBody, BroadcastAsyncPostResponse, BroadcastPostRequestParams>(config);
            return await this.trackAsyncJobProgress(result.job.id);
        }

        const request = new CreateBroadcastViewRequest({ viewId: viewId, overwrite: overwrite, showTabs: showTabs, showWatermark: showWatermark, acceptTermsOfUse: acceptTermsOfUse });
        return WebHttpClientWithErrorToastInstance.sendRequest(request)
            .then(LongRunningAction.getActionIdFromActionResult)
            .then((actionId: string) =>
                LongRunningAction.trackAsyncJobProgressWithPromise(actionId));
    }

    public deleteBroadcast(viewLuid: string): Promise<IDeleteBroadcastResult | {}> {

        if (FeatureFlags.broadcastUIRest) {

            const config: RestApiRequestConfig<{}, {}> = {
                ...createBaseRestConfig(),
                httpMethod: 'DELETE',
                endpoint: `broadcasts/views/${viewLuid}`,
            };

            return makeRestApiRequest<{}, {}, {}>(config);
        }
        return WebHttpClient.sendRequest(new DeleteBroadcastRequest({ viewLuid: viewLuid }));
    }

    private async getViewsFromListBroadcastViews(params: IGetItemsParams): Promise<IGetViewsResult> {
        const sortClause = Order.serialize(params.order);
        // The WebClient APIs that MixedContentSagas uses sends the startIndex from the entire array of items.
        // For example: After fetching the first 100 items, startIndex=100 to fetch the next 100 items.
        // In our ListBroadcastViews API, we use pages instead, so we need to convert the startIndex to a pageNumber.
        const pageNumberFromResourceIndex = params.page.startIndex / params.page.maxItems + 1;
        const config: RestApiRequestConfig<{}, {}> = {
            ...createBaseRestConfig(),
            httpMethod: 'GET',
            endpoint: `broadcasts/views?pageNumber=${pageNumberFromResourceIndex}&pageSize=${params.page.maxItems}${sortClause ? ('&sort=' + sortClause) : ''}`,
        };

        // Grab the View IDs of Broadcasts from listBroadcastView
        const listBroadcastViewsResponse = await makeRestApiRequest<{}, BroadcastListViewsResponse>(config);

        // Use the View IDs to grab the metadata for Broadcasts using getViews
        const viewIds: string[] = listBroadcastViewsResponse.broadcastViews.broadcast.map(broadcastView => broadcastView.view.id);
        const viewIdFilter = [Filter.stringFieldInArray(FilterFieldNames.luid, viewIds)];
        const getViewResponse = await WebHttpClient.sendRequest(new GetViewsRequest({
            filter: formatFilterClauses(viewIdFilter),
            order: params.order
        }));

        // The true pagination comes from the listBroadcastView request
        const pagination = listBroadcastViewsResponse.pagination;
        const pageNumber = Number(pagination.pageNumber);
        const pageSize = Number(pagination.pageSize);
        const totalAvailable = Number(pagination.totalAvailable);

        const modifiedGetViewResponse = {
            ...getViewResponse,
            totalCount: totalAvailable,
            moreItems: pageNumber * pageSize < totalAvailable
        };
        return modifiedGetViewResponse;
    }

    private async trackAsyncJobProgress(jobLuid: string, pollInterval: number = 1000, timeout: number = 50000): Promise<JobStatusResponse> {
        const config: RestApiRequestConfig<{}, {}> = {
            ...createBaseRestConfig(),
            httpMethod: 'GET',
            endpoint: `jobs/${jobLuid}`,
        };

        const startTime = Date.now();

        return new Promise<JobStatusResponse>((resolve, reject) => {
            const timer = setInterval(async () => {
                try {
                    const response = await makeRestApiRequest<{}, JobStatusResponse, {}>(config);
                    const job = response.job;
                    if (job.progress === '100') {
                        clearInterval(timer);
                        if (job.finishCode === '0') {
                            resolve(response);
                        } else {
                            reject(this.constructErrorResponseForFailedJob(response));
                        }
                    } else if (Date.now() - startTime > timeout) {
                        clearInterval(timer);
                        reject(new Error('Job Polling timed out.'));
                    }
                } catch (error) {
                    clearInterval(timer);
                    reject(new Error(`Error while tracking job progress: ${error.message}`));
                }
            }, pollInterval);
        });
    }

    private constructErrorResponseForFailedJob(response: JobStatusResponse): RestApiErrorResponse {
        const statusNote = response.job.statusNotes?.statusNote;
        const errorResponse: RestApiErrorResponse = {
            errorSource: RestErrorSource.Server,
            data: {
                error: {
                    detail: statusNote?.find((note: any) => note.type === 'errorDetail')?.value,
                    code: statusNote?.find((note: any) => note.type === 'errorCode')?.value,
                    summary: '',
                }
            },
            statusText: 'Request Failed',
            status: 400
        };
        return errorResponse;
    }
}

function createBaseRestConfig(): Omit<RestApiRequestConfig<{}, {}>, 'endpoint' | 'httpMethod'> {
    return {
        restApiVersion: '3.19',
        requestBody: {}
    };
}

export const Broadcasts = new BroadcastsResource();
